import { useState } from "react";
import client, { apiErr } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Camera, Loader2 } from "lucide-react";
import { toast } from "sonner";

export const PHOTO_REQ = [["arrival", "Arrival"], ["before", "Before"], ["after", "After"], ["completion", "Completion"]];

export const PhotoTypeChecks = ({ value, onChange, prefix, disabled }) => (
  <div className="flex flex-wrap gap-4">
    {PHOTO_REQ.map(([k, label]) => (
      <label key={k} className="flex items-center gap-2 text-sm text-slate-700">
        <Checkbox data-testid={`${prefix}-${k}`} disabled={disabled} checked={value.includes(k)}
          onCheckedChange={(on) => onChange(on ? [...value, k] : value.filter((x) => x !== k))} />
        {label}
      </label>
    ))}
  </div>
);

const labels = (list) => (list.length ? list.map((k) => PHOTO_REQ.find((p) => p[0] === k)?.[1] || k).join(", ") : "None");

export const RequiredPhotos = ({ job, canManage, onSaved }) => {
  const [editing, setEditing] = useState(false);
  const [useSite, setUseSite] = useState(true);
  const [pick, setPick] = useState([]);
  const [series, setSeries] = useState(false);
  const [busy, setBusy] = useState(false);
  const own = job.jobRequiredPhotoTypes;

  const start = () => { setUseSite(own == null); setPick(own ?? job.siteRequiredPhotoTypes ?? []); setSeries(false); setEditing(true); };
  const save = async () => {
    setBusy(true);
    try {
      await client.put(`/jobs/${job.id}/required-photos`, { types: useSite ? null : pick, applyToSeries: series });
      toast.success("Required photos saved"); setEditing(false); onSaved?.();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <Card className="p-5 border-slate-200 space-y-3" data-testid="job-required-photos">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <p className="font-semibold text-slate-900 flex items-center gap-2"><Camera className="h-4 w-4 text-brand" />Required photos</p>
        {canManage && !editing && <Button size="sm" variant="outline" onClick={start} data-testid="job-required-photos-edit">Edit</Button>}
      </div>
      {!editing ? (
        <p className="text-sm text-slate-700" data-testid="job-required-photos-value">
          {labels(job.requiredPhotoTypes || [])}
          <Badge variant="outline" className="ml-2 font-normal text-xs">{own == null ? "Site default" : "Set on this job"}</Badge>
        </p>
      ) : (
        <div className="space-y-3">
          <label className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2">
            <span className="text-sm text-slate-700">Use the site's default ({labels(job.siteRequiredPhotoTypes || [])})</span>
            <Switch checked={useSite} onCheckedChange={setUseSite} data-testid="job-required-photos-use-site" />
          </label>
          <PhotoTypeChecks value={pick} onChange={setPick} prefix="job-required-photo" disabled={useSite} />
          {job.seriesId && (
            <label className="flex items-center gap-2 text-sm text-slate-600">
              <Checkbox checked={series} onCheckedChange={(v) => setSeries(!!v)} data-testid="job-required-photos-series" />Apply to this and all future visits
            </label>
          )}
          <p className="text-xs text-slate-500">Cleaners can't end the job in the app until each required type has a photo.</p>
          <div className="flex gap-2">
            <Button size="sm" onClick={save} disabled={busy} className="bg-brand hover:bg-brand-dark" data-testid="job-required-photos-save">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}</Button>
            <Button size="sm" variant="ghost" onClick={() => setEditing(false)} data-testid="job-required-photos-cancel">Cancel</Button>
          </div>
        </div>
      )}
    </Card>
  );
};
